<template>
  <van-tabbar v-model="active" :z-index="9" route>
    <van-tabbar-item
      v-for="(item, index) in tabbars"
      :key="index"
      :to="{ path: item.url, query: route.query }"
      :class="{ 'active-item': index === active }"
      replace
      @click="onClickTabbarItem(item)"
    >
      <template #icon>
        <GlobalImage v-show="index === active" fit="contain" :src="item.active" class="tab-icon" />
        <GlobalImage v-show="index !== active" fit="contain" :src="item.normal" class="tab-icon" />
      </template>
      <span v-show="index > 0 || active > 0">{{ item.title }}</span>

      <div
        v-if="item?.badge"
        class="item-badge"
        :class="{
          scaling: item?.animation === 'scale',
          shakingScaling: item?.animation === 'shake_scale',
          showRotateHide: item?.animation === 'appear_flip_disappear',
          shaking: item?.animation === 'shake_only',
          swipe: isSwipeBadge(item),
        }"
        @animationend="onBadgeAnimationEnd(item)"
      >
        <van-swipe v-if="isSwipeBadge(item)" vertical :autoplay="2000" :touchable="false" :duration="0" :show-indicators="false" @change="onSwipeChanged(item)">
          <van-swipe-item v-for="(bd, j) in getBadgeList(item?.badge)" :key="j">{{ bd }}</van-swipe-item>
        </van-swipe>
        <span v-else class="badge-text">{{ item?.badge }}</span>
      </div>
    </van-tabbar-item>
  </van-tabbar>
</template>

<script setup>
import { ref, onBeforeMount, reactive, watch, onMounted } from 'vue'
import { getImageUrl, isHideShowComment } from '@/utils'
import { req } from '@/api'
import { store } from '@/pinia/store'
import { useRoute } from 'vue-router'

const route = useRoute()
const storeData = store()
const active = ref(0)
const tabbars = reactive([
  {
    title: '首页',
    normal: getImageUrl('homePage', 'tab_icon_01.png'),
    active: getImageUrl('homePage', 'tab_icon_01_act.png'),
    url: '/index',
    index: 'homeTab_h5',
    swiperChanged: false,
  },
  {
    title: '技师',
    normal: getImageUrl('homePage', 'tab_icon_02.png'),
    active: getImageUrl('homePage', 'tab_icon_02_act.png'),
    url: '/technicianModule/technicianList/index',
    index: 'techTab_h5',
    swiperChanged: false,
  },
  {
    title: '发现',
    normal: getImageUrl('homePage', 'tab_icon_03.png'),
    active: getImageUrl('homePage', 'tab_icon_03_act.png'),
    url: '/trendsModule/trends/index',
    index: 'trendTab_h5',
    swiperChanged: false,
  },
  {
    title: '订单',
    normal: getImageUrl('homePage', 'tab_icon_04.png'),
    active: getImageUrl('homePage', 'tab_icon_04_act.png'),
    url: '/orderModule/myOrders/index',
    index: 'orderTab_h5',
    swiperChanged: false,
  },
  {
    title: '我的',
    normal: getImageUrl('homePage', 'tab_icon_05.png'),
    active: getImageUrl('homePage', 'tab_icon_05_act.png'),
    url: '/myModule/home/index',
    index: 'mineTab_h5',
    swiperChanged: false,
  },
])

watch(
  () => route.path,
  (n) => {
    const index = tabbars.findIndex((x) => x.url === n)
    if (index > -1) {
      active.value = index
    }
  },
)

const isSwipeBadge = (item) => item.animation && item?.badge?.indexOf(',') > -1

const getDismissDynamicBadges = () => {
  try {
    return JSON.parse(localStorage.getItem(`dismiss_tabbar_dynamic_badge`) || '[]')
  } catch (e) {
    return []
  }
}

const getDismissStaticBadges = () => {
  try {
    return JSON.parse(localStorage.getItem(`dismiss_tabbar_static_badge`) || '[]')
  } catch (e) {
    return []
  }
}

const setupTabbarBadge = () => {
  const dismissDynamicBadges = getDismissDynamicBadges()
  const dismissStaticBadges = getDismissStaticBadges()

  tabbars.forEach((item) => {
    const { id, dynamicText, staticText, dynamicShowCount, dynamicAnimationType, staticShowCount } = item
    const dynamicDismissed = dismissDynamicBadges.includes(id)
    const staticDismissed = dismissStaticBadges.includes(id)

    if (dynamicText && dynamicShowCount === 'every_launch') {
      item.badge = dynamicText
      item.animation = dynamicAnimationType
    } else if (dynamicText && dynamicShowCount === 'show_once' && !dynamicDismissed) {
      item.badge = dynamicText
      item.animation = dynamicAnimationType
    } else if (staticText && staticShowCount === 'always') {
      item.badge = staticText
      item.animation = ''
    } else if (staticText && staticShowCount === 'dismiss_on_click' && !staticDismissed) {
      item.badge = staticText
      item.animation = ''
    } else {
      item.badge = ''
      item.animation = ''
    }
  })
}

const initTabbar = async () => {
  const res = await req.getNavIcon({ market: 'wechat' }).catch(() => null)
  if (res?.code === 0) {
    res.data?.forEach((item) => {
      const { icon, index } = item
      const originItem = tabbars.find((x) => x.index === index)
      if (!originItem) return

      originItem.id = icon.id
      originItem.normal = icon.iconUnselected || originItem.normal
      originItem.active = icon.iconSelected || originItem.active
      originItem.staticText = icon.staticText
      originItem.staticShowCount = icon.staticShowCount
      originItem.dynamicText = icon.dynamicText
      originItem.dynamicShowCount = icon.dynamicShowCount
      originItem.dynamicAnimationType = icon.dynamicAnimationType
    })
    setupTabbarBadge()
  }
}

const onClickTabbarItem = (item) => {
  // 点击tabbar item时，仅静态角标的消失逻辑为dismiss_on_click时，隐藏角标。动态角标不受点击逻辑影响
  if (item.badge && item.staticShowCount === 'dismiss_on_click') {
    dismissStaticBadge(item)
    if (item.animation) {
      // 与dismiss逻辑双重兜底，确保badge能消失
      item.staticText = ''
    } else {
      item.badge = ''
    }
  }

  storeData.isFirstVisitHome = true
  if (active.value == 1) {
    storeData.umengOrder.origin = '技师列表'
  } else if (active.value == 2) {
    storeData.umengOrder.origin = '发现'
  } else if (active.value == 3) {
    storeData.umengOrder.origin = '我的订单'
  } else if (active.value == 4) {
    storeData.umengOrder.origin = '我的页面'
  }
}

// badge分割
const getBadgeList = (badge) => {
  return badge.split(',')
}

const onSwipeChanged = (item) => {
  item.swiperChanged = true
}

// badge 动画播放完毕回调
const onBadgeAnimationEnd = (item) => {
  item.animation = ''
  if (item.dynamicShowCount === 'show_once') dismissDynamicBadge(item)
  if (!item.badge) return

  const dismissStaticBadges = getDismissStaticBadges()
  if (item.staticText && item.staticShowCount === 'always') {
    item.badge = item.staticText
  } else if (item.staticText && item.staticShowCount === 'dismiss_on_click' && !dismissStaticBadges.includes(item.id)) {
    item.badge = item.staticText
  } else {
    item.badge = ''
  }
}

const dismissStaticBadge = (item) => {
  const dismissBadges = getDismissStaticBadges()
  if (dismissBadges.includes(item.id)) return
  dismissBadges.push(item.id)
  localStorage.setItem(`dismiss_tabbar_static_badge`, JSON.stringify(dismissBadges))
}

const dismissDynamicBadge = (item) => {
  const dismissBadges = getDismissDynamicBadges()
  if (dismissBadges.includes(item.id)) return
  dismissBadges.push(item.id)
  localStorage.setItem(`dismiss_tabbar_dynamic_badge`, JSON.stringify(dismissBadges))
}

onBeforeMount(() => {
  if (isHideShowComment()) {
    tabbars.splice(2, 1)
  }
  const currentIndex = tabbars.findIndex((item) => route.path.startsWith(item.url))
  if (currentIndex !== -1) {
    active.value = currentIndex
  }
})

onMounted(() => {
  initTabbar()
})
</script>

<style lang="scss">
/* 优化动画性能，使用transform和opacity属性 */

/* 动效1：入场弹出（锚点左下角，弹出1s + 静止1s，总周期2s循环） */
@keyframes scaling {
  0% {
    transform: scale(0);
  }
  16.5% {
    transform: scale(1.15);
  }
  33% {
    transform: scale(0.95);
  }
  50% {
    transform: scale(1);
  }
  /* 50%~100% 保持静止1s，下个周期重新弹出 */
  100% {
    transform: scale(1);
  }
}

/* 动效2：呼吸闪烁（锚点左下角，闪烁1s + 静止1s，总周期2s循环） */
@keyframes shakingScaling {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  25% {
    transform: scale(0.5);
    opacity: 0;
  }
  50% {
    transform: scale(1);
    opacity: 1;
  }
  /* 50%~100% 保持静止1s，下个周期重新闪烁 */
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

/* 动效3：左右扇动（锚点正中心，3D图层Y轴旋转+透视700px，扇动1s + 静止1s，总周期2s循环） */
@keyframes showRotateHide {
  0% {
    transform: perspective(700px) rotateY(0deg);
  }
  12.5% {
    transform: perspective(700px) rotateY(-25deg);
  }
  25% {
    transform: perspective(700px) rotateY(0deg);
  }
  37.5% {
    transform: perspective(700px) rotateY(25deg);
  }
  50% {
    transform: perspective(700px) rotateY(0deg);
  }
  /* 50%~100% 保持静止1s，下个周期重新扇动 */
  100% {
    transform: perspective(700px) rotateY(0deg);
  }
}

/* 动效4：仅晃动（根部锁死左下角，晃动1s + 静止1s，总周期2s循环）  */
@keyframes shaking {
  0% {
    transform: rotateZ(0deg);
  }
  12.5% {
    transform: rotateZ(-7deg);
  }
  25% {
    transform: rotateZ(7deg);
  }
  37.5% {
    transform: rotateZ(-4deg);
  }
  50% {
    transform: rotateZ(0deg);
  }
  /* 50%~100% 保持静止1s，下个周期重新晃动 */
  100% {
    transform: rotateZ(0deg);
  }
}

.van-tabbar {
  height: 52px;
  .van-tabbar-item {
    position: relative;
    padding: 6px 0 9px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    .van-tabbar-item__icon {
      width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 0;
      .tab-icon {
        width: 20px;
        height: 20px;
        img {
          width: 20px;
          height: 20px;
        }
      }
    }
    .item-badge {
      position: absolute;
      top: -4px;
      left: 50%;
      width: fit-content;
      height: 16px;
      padding: 0 5px;
      border-radius: 8px 8px 8px 2px;
      background: #ff4d4f;
      overflow: hidden;
      box-sizing: border-box;
      z-index: 99;
      font-size: 8px;
      color: #ffffff;
      text-overflow: ellipsis;
      text-align: center;
      white-space: nowrap;
      will-change: transform;

      .van-swipe {
        height: 16px;
        line-height: 16px;

        .van-swipe-item {
          position: relative;
          top: 0.5px;
          height: 16px;
          display: flex;
          justify-content: center;
          align-items: center;
          font-size: 9px;
          color: #ffffff;
          white-space: nowrap;
          overflow: hidden;
        }
      }

      .badge-text {
        position: relative;
        top: 0.5px;
        height: 16px;
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 9px;
        white-space: nowrap;
        overflow: hidden;
      }

      /* 动效1：入场弹出，弹出1s后间隔1s再循环（总周期2s） */
      &.scaling {
        -webkit-animation: scaling 2s 2 linear;
        animation: scaling 2s 2 linear;
        -webkit-animation-fill-mode: backwards;
        animation-fill-mode: backwards;
        transform-origin: left bottom;
      }

      /* 动效2：呼吸闪烁，闪烁1s后间隔1s再循环（总周期2s） */
      &.shakingScaling {
        -webkit-animation: shakingScaling 2s 2 linear;
        animation: shakingScaling 2s 2 linear;
        transform-origin: left bottom;
      }

      /* 动效3：左右扇动，扇动1s后间隔1s再循环（总周期2s） */
      &.showRotateHide {
        -webkit-animation: showRotateHide 2s 2 linear;
        animation: showRotateHide 2s 2 linear;
        transform-origin: center;
      }

      /* 动效4：仅晃动，晃动1s后间隔1s再循环（总周期2s） */
      &.shaking {
        -webkit-animation: shaking 2s 2 linear;
        animation: shaking 2s 2 linear;
        transform-origin: left bottom;
      }

      &.swipe {
        -webkit-animation-iteration-count: 4;
        animation-iteration-count: 4;
      }
    }
    .van-tabbar-item__text {
      font-size: 10px;
      color: #1f1f1f;
      span {
        line-height: 1;
      }
    }
    &:first-of-type {
      &.active-item {
        .van-tabbar-item__icon {
          width: 40px;
          height: 40px;
          .tab-icon {
            width: 40px;
            height: 40px;
            img {
              width: 40px;
              height: 40px;
            }
          }
        }
      }
    }
    &.active-item {
      .van-tabbar-item__text {
        color: $colorYellow;
      }
    }
  }
}
</style>
